/**
 * Badge Component
 *
 * Small label component for displaying status, tags, or categories.
 * Supports multiple variants for different use cases.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant = 'default', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          // Variant styles
          {
            // Default - Primary badge
            'border-transparent bg-primary-500 text-white': variant === 'default',
            // Secondary - Neutral badge
            'border-transparent bg-gray-200 text-gray-900': variant === 'secondary',
            // Destructive - Error or warning badge
            'border-transparent bg-red-600 text-white': variant === 'destructive',
            // Outline - Bordered badge
            'border-gray-300 bg-transparent text-gray-900': variant === 'outline',
            // Success - Positive status
            'border-transparent bg-green-600 text-white': variant === 'success',
            // Warning - Caution status
            'border-transparent bg-yellow-500 text-white': variant === 'warning',
          },
          className
        )}
        {...props}
      />
    );
  }
);
Badge.displayName = 'Badge';

export { Badge };
